import { useEffect } from 'react';

import { ApolloQueryResult } from '@apollo/client';
import { useGraphQLMutation } from '@betterment/graphql';
import { Banner, Link, Text } from '@betterment/samba-react';

import { CsvExportStatus } from '../../../../graphql/schema';
import { DismissClientActivityCsvExportDocument } from '../../graphql/mutation.dismissCSVDownloadBanner.generated';
import { ClientActivityCsvExportQuery } from '../../graphql/query.clientActivityCSVExport.generated';

type BannerVariants = 'success' | 'warning' | 'info';
export const STOP_POLLING_STATUSES = [CsvExportStatus.Complete, CsvExportStatus.Error];

const bannerVariants: Record<CsvExportStatus, { variant: BannerVariants; copy: string }> = {
  [CsvExportStatus.Generating]: { variant: 'info', copy: 'Generating CSV. This may take a few minutes.' },
  [CsvExportStatus.Complete]: { variant: 'success', copy: 'Your CSV file is ready.' },
  [CsvExportStatus.Error]: { variant: 'warning', copy: 'CSV generation failed. Please try again later.' },
};

export const CSVDownloadBanner = ({
  startPolling,
  activityCSVReportStatus,
  refetchBannerStatus,
}: {
  startPolling: (pollInterval: number) => void;
  activityCSVReportStatus: CsvExportStatus;
  refetchBannerStatus: () => Promise<ApolloQueryResult<ClientActivityCsvExportQuery>>;
}) => {
  useEffect(() => {
    if (activityCSVReportStatus) {
      startPolling(30000);
    }
  }, [activityCSVReportStatus, startPolling]);

  const [dismissBanner] = useGraphQLMutation(DismissClientActivityCsvExportDocument);

  if (!activityCSVReportStatus) {
    return null;
  }

  const isGenerating = activityCSVReportStatus === CsvExportStatus.Generating;
  const isCompleted = activityCSVReportStatus === CsvExportStatus.Complete;
  const { variant, copy } = bannerVariants[activityCSVReportStatus];

  const handleDismiss = async () => {
    await dismissBanner();
    await refetchBannerStatus();
  };

  return (
    <Banner variant={variant} css={{ mb: 'space.300' }} data-testid="csv-download-banner">
      <Banner.Content>
        <Text as="p">
          {copy}{' '}
          {isCompleted && (
            <Link href="/advisor_client_activity_csv_export" download="transactions.csv">
              Download
            </Link>
          )}
        </Text>
      </Banner.Content>
      {!isGenerating && (
        <Banner.Close
          onClick={() => {
            void handleDismiss();
          }}
          data-testid="csv-download-banner-close-button"
        />
      )}
    </Banner>
  );
};
